/**
 * Cliente HTTP da API. Em desenvolvimento, o Vite repassa /api para a API local.
 * O cookie de sessão (httpOnly) vai automaticamente: o JavaScript nunca o lê.
 */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly motivo?: string,
    readonly campos?: { campo: string; mensagem: string }[],
  ) {
    super(message);
    this.name = 'ErroApi';
  }
}

type Ouvinte = () => void;
const aoExpirarSessao = new Set<Ouvinte>();
let ultimaAtividade = Date.now();

/** Avisa quando a API responder "sessão expirada" (o app volta para o login). */
export function ouvirSessaoExpirada(ouvinte: Ouvinte): () => void {
  aoExpirarSessao.add(ouvinte);
  return () => aoExpirarSessao.delete(ouvinte);
}

/** Momento da última comunicação com a API (cada requisição renova a sessão no servidor). */
export function momentoUltimaAtividade(): number {
  return ultimaAtividade;
}

async function lerJson(resposta: Response): Promise<unknown> {
  try {
    return await resposta.json();
  } catch {
    return undefined;
  }
}

export async function requisitar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(`/api${caminho}`, {
      credentials: 'include',
      ...opcoes,
      headers: { Accept: 'application/json', ...opcoes.headers },
    });
  } catch {
    throw new ErroApi(0, 'Sem conexão com o servidor. Verifique sua internet e tente novamente.');
  }
  const corpo = (await lerJson(resposta)) as
    { erro?: string; motivo?: string; campos?: { campo: string; mensagem: string }[] } | undefined;
  if (!resposta.ok) {
    if (resposta.status === 401 && corpo?.motivo === 'sessao_expirada')
      aoExpirarSessao.forEach((o) => o());
    throw new ErroApi(
      resposta.status,
      corpo?.erro ?? 'Erro de comunicação com o servidor.',
      corpo?.motivo,
      corpo?.campos,
    );
  }
  ultimaAtividade = Date.now();
  return corpo as T;
}

/** POST/PUT com corpo JSON. */
export function enviar<T>(caminho: string, metodo: 'POST' | 'PUT', corpo?: unknown): Promise<T> {
  return requisitar<T>(caminho, {
    method: metodo,
    headers: { 'Content-Type': 'application/json' },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
  });
}

export interface Saude {
  status: 'ok' | 'degradado';
  ambiente: string;
  banco: 'ok' | 'indisponivel';
  horario: string;
}

/** A API responde 503 (com corpo) quando o banco está fora: isso é um estado, não um erro. */
export async function obterSaude(): Promise<Saude> {
  const resposta = await fetch('/api/saude', { headers: { Accept: 'application/json' } });
  if (resposta.status !== 200 && resposta.status !== 503) {
    throw new ErroApi(resposta.status, 'API indisponível.');
  }
  return (await resposta.json()) as Saude;
}
